/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import { Card, getCardVariables, replaceVariables } from 'carbon-addons-iot-react';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import { RouteComponentProps, withRouter } from 'react-router';
import { Launch32 } from '@carbon/icons-react';
import { defineMessages, useIntl } from 'react-intl';

import { openThirdPartyURLTab } from '../../utils/thirdPartyContentUtils';

import { ErrorBoundary } from 'Common';

const messages = defineMessages({
  openInNewTabTitle: 'Open in new tab',
});

interface ThirdPartyCardProps
  extends RouteComponentProps<{ hierarchyName: string; entityId: string; entityTypeId: string }> {
  id: string;
  content: JSX.Element;
  customContent: { type: string; value: string };
  data?: Array<Record<string, unknown>>;
  title?: string;
  cardVariables?: Record<string, unknown>;
  startDate?: Date;
  endDate?: Date;
}

const defaultProps = {
  data: [],
  title: null,
  cardVariables: {},
  startDate: null,
  endDate: null,
};

const ThirdPartyCard = ({
  id,
  content,
  customContent,
  data,
  title,
  startDate,
  endDate,
  match,
  ...cardProps
}: ThirdPartyCardProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const card = useMemo(
    () => ({
      id,
      content,
      customContent,
      title,
      data,
      ...omit(cardProps, ['match', 'location']),
    }),
    [customContent, cardProps, content, data, id, title]
  );

  // get resource name from URL params.. for now
  const resourceName = useMemo(
    () => match?.params?.hierarchyName || match?.params?.entityId || match?.params?.entityTypeId,
    [match?.params]
  );

  // replace variables
  const { cardVariables } = card;
  const variableNames = useMemo(() => getCardVariables(card), [card]);
  const variablesData = useMemo(
    () => ({
      ...cardVariables,
      resourceName,
      startDateTimestamp: startDate.toISOString(),
      endDateTimestamp: endDate.toISOString(),
    }),
    [cardVariables, startDate, endDate, resourceName]
  );
  const updatedCard = useMemo(
    () => replaceVariables(variableNames, variablesData, card),
    [card, variableNames, variablesData]
  );

  const updatedCardWithExtraActions = useMemo(
    () => ({
      ...updatedCard,
      extraActions: {
        id: `open-in-new-tab-${updatedCard.id}`,
        icon: Launch32,
        iconDescription: formatMessage(messages.openInNewTabTitle),
        callback: (): void => openThirdPartyURLTab(updatedCard.customContent, updatedCard.data),
      },
    }),
    [updatedCard, formatMessage]
  );
  return (
    <ErrorBoundary>
      <Card hideHeader={isNil(title)} {...updatedCardWithExtraActions}>
        {content}
      </Card>
    </ErrorBoundary>
  );
};

ThirdPartyCard.defaultProps = defaultProps;
export default withRouter(ThirdPartyCard);

/* eslint-enable react/jsx-props-no-spreading */
